import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FlaskConical, Plus, TrendingUp, BookOpen, Dna, Play, CheckCircle } from 'lucide-react'
import { Card, Badge, PageHeader, EmptyState, MetricCard } from '../components/Card'
import {
  listExperiments, createExperiment, startExperiment, concludeExperiment,
  experimentResults, listLearnings, analyticsGenome,
  type Exp, type ConcludeResult, type Learning, type GenomeInsight,
} from '../api/client'
import { layout } from '../utils/layout'

const STATUS_COLOR: Record<string, 'slate' | 'yellow' | 'green' | 'blue'> = {
  draft: 'slate',
  running: 'yellow',
  completed: 'green',
  cancelled: 'blue',
}

const LEARNING_TYPE_COLOR: Record<string, 'slate' | 'yellow' | 'green' | 'blue'> = {
  hook: 'blue',
  cta: 'yellow',
  format: 'green',
  timing: 'slate',
  hashtag: 'yellow',
}

export default function ExperimentsView() {
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState<'experiments' | 'learnings' | 'genome'>('experiments')
  const [showCreate, setShowCreate] = useState(false)
  const [selected, setSelected] = useState<Exp | null>(null)
  const [concludeResult, setConcludeResult] = useState<ConcludeResult | null>(null)
  const [form, setForm] = useState({
    name: '', variant_draft_ids: '', metric: 'engagement_rate',
    hypothesis: '', min_sample_size: 100,
  })

  const { data: expData } = useQuery({
    queryKey: ['experiments'],
    queryFn: () => listExperiments(),
  })

  const { data: learningsData } = useQuery({
    queryKey: ['learnings'],
    queryFn: () => listLearnings(),
    enabled: activeTab === 'learnings',
  })

  const { data: genomeData } = useQuery({
    queryKey: ['genome'],
    queryFn: () => analyticsGenome(),
    enabled: activeTab === 'genome',
  })

  const createMut = useMutation({
    mutationFn: () => createExperiment({
      name: form.name,
      variant_draft_ids: form.variant_draft_ids.split(',').map(s => s.trim()).filter(Boolean),
      metric: form.metric,
      hypothesis: form.hypothesis || undefined,
      min_sample_size: form.min_sample_size,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['experiments'] }); setShowCreate(false) },
  })

  const startMut = useMutation({
    mutationFn: (id: string) => startExperiment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['experiments'] }),
  })

  const concludeMut = useMutation({
    mutationFn: (id: string) => concludeExperiment(id),
    onSuccess: (data) => {
      setConcludeResult(data as ConcludeResult)
      qc.invalidateQueries({ queryKey: ['experiments'] })
      qc.invalidateQueries({ queryKey: ['learnings'] })
    },
  })

  const resultsMut = useMutation({
    mutationFn: (id: string) => experimentResults(id),
    onSuccess: (data) => {
      const r = (data as { results: Record<string, unknown> }).results
      if (r) setConcludeResult(r as unknown as ConcludeResult)
    },
  })

  const METRICS = ['engagement_rate', 'click_rate', 'save_rate', 'reach', 'conversions']

  return (
    <div>
      <PageHeader
        title="Experiments"
        subtitle="A/B tests · Statistical significance · Content genome · Predictive scoring"
        action={
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> New Experiment
          </button>
        }
      />

      {/* Tab bar */}
      <div className="px-6 pt-2 flex gap-1 border-b border-surface-border">
        {([
          { id: 'experiments', icon: FlaskConical, label: 'Experiments' },
          { id: 'learnings', icon: BookOpen, label: 'Learnings' },
          { id: 'genome', icon: Dna, label: 'Content Genome' },
        ] as const).map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition-colors ${
              activeTab === id
                ? 'border-brand-500 text-brand-400'
                : 'border-transparent text-slate-400 hover:text-slate-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {id === 'experiments' && expData?.total ? (
              <span className="bg-slate-700 text-slate-300 text-xs rounded-full px-1.5">{expData.total}</span>
            ) : null}
            {id === 'learnings' && learningsData?.total ? (
              <span className="bg-slate-700 text-slate-300 text-xs rounded-full px-1.5">{learningsData.total}</span>
            ) : null}
          </button>
        ))}
      </div>

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>

        {/* ── Experiments Tab ── */}
        {activeTab === 'experiments' && (
          <div className="flex gap-4">
            <div className="flex-1 min-w-0">
              <Card title={`${expData?.total ?? 0} Experiments`}>
                {!expData?.experiments.length ? (
                  <EmptyState
                    icon={<FlaskConical className="w-12 h-12" />}
                    title="No experiments yet"
                    description="Create an A/B test from two or more content drafts. SocialScale tracks performance and determines the winner with statistical significance (95% confidence by default)."
                    action={
                      <button onClick={() => setShowCreate(true)} className="bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg">
                        Create Experiment
                      </button>
                    }
                  />
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-500 border-b border-surface-border">
                        <th className="text-left pb-2">Name</th>
                        <th className="text-left pb-2">Metric</th>
                        <th className="text-right pb-2">Lift</th>
                        <th className="text-right pb-2">Winner</th>
                        <th className="text-right pb-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expData.experiments.map(e => (
                        <tr
                          key={e.id}
                          onClick={() => { setSelected(e); setConcludeResult(null) }}
                          className={`border-b border-surface-border/50 hover:bg-slate-800/30 cursor-pointer ${selected?.id === e.id ? 'bg-slate-800/50' : ''}`}
                        >
                          <td className="py-2 font-medium">{e.name}</td>
                          <td className="py-2 text-slate-400 text-xs">{e.metric}</td>
                          <td className="py-2 text-right">
                            {e.lift !== undefined && e.lift !== null ? (
                              <span className={e.lift >= 0 ? 'text-green-400' : 'text-red-400'}>
                                {e.lift >= 0 ? '+' : ''}{e.lift.toFixed(1)}%
                              </span>
                            ) : '—'}
                          </td>
                          <td className="py-2 text-right text-xs text-slate-400">{e.winner ?? '—'}</td>
                          <td className="py-2 text-right">
                            <Badge color={STATUS_COLOR[e.status] ?? 'slate'}>{e.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </div>

            {/* Detail panel */}
            {selected && (
              <div className="w-80 shrink-0 space-y-3">
                <Card title="Experiment">
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-slate-500 mb-0.5">Name</p>
                      <p className="text-sm font-medium text-slate-200">{selected.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-0.5">Metric</p>
                      <p className="text-sm text-slate-300">{selected.metric}</p>
                    </div>
                    {selected.winner && (
                      <div>
                        <p className="text-xs text-slate-500 mb-0.5">Winner</p>
                        <p className="text-sm text-green-400 font-medium">{selected.winner}</p>
                      </div>
                    )}
                    {selected.lift !== undefined && selected.lift !== null && (
                      <div>
                        <p className="text-xs text-slate-500 mb-0.5">Lift</p>
                        <p className={`text-xl font-bold ${selected.lift >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {selected.lift >= 0 ? '+' : ''}{selected.lift.toFixed(1)}%
                        </p>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="space-y-2 pt-1">
                      {selected.status === 'draft' && (
                        <button
                          onClick={() => startMut.mutate(selected.id)}
                          disabled={startMut.isPending}
                          className="w-full flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white text-xs py-2 rounded-lg transition-colors"
                        >
                          <Play className="w-3.5 h-3.5" />
                          {startMut.isPending ? 'Starting…' : 'Start Experiment'}
                        </button>
                      )}
                      {selected.status === 'running' && (
                        <button
                          onClick={() => concludeMut.mutate(selected.id)}
                          disabled={concludeMut.isPending}
                          className="w-full flex items-center justify-center gap-2 bg-green-700 hover:bg-green-600 text-white text-xs py-2 rounded-lg transition-colors"
                        >
                          <CheckCircle className="w-3.5 h-3.5" />
                          {concludeMut.isPending ? 'Analysing…' : 'Conclude & Determine Winner'}
                        </button>
                      )}
                      {selected.status === 'completed' && (
                        <button
                          onClick={() => resultsMut.mutate(selected.id)}
                          disabled={resultsMut.isPending}
                          className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-xs py-2 rounded-lg"
                        >
                          <TrendingUp className="w-3.5 h-3.5" />
                          {resultsMut.isPending ? 'Loading…' : 'View Results'}
                        </button>
                      )}
                    </div>

                    {/* Conclude / results display */}
                    {concludeResult && (
                      <div className="bg-slate-800 rounded-lg p-3 space-y-2 text-xs">
                        <p className="font-semibold text-slate-200">
                          {typeof concludeResult.conclusion === 'string'
                            ? concludeResult.conclusion
                            : JSON.stringify(concludeResult.conclusion)}
                        </p>
                        {concludeResult.winner && (
                          <p className="text-green-400">Winner: {concludeResult.winner}</p>
                        )}
                        {concludeResult.explanation && (
                          <p className="text-slate-400">{concludeResult.explanation}</p>
                        )}
                        {concludeResult.p_value !== undefined && (
                          <div className="flex gap-4 text-slate-400 pt-1">
                            <span>p={concludeResult.p_value.toFixed(4)}</span>
                            <span>conf={(concludeResult.confidence * 100).toFixed(1)}%</span>
                          </div>
                        )}
                        {concludeResult.learning_stored && concludeResult.learning && (
                          <div className="border-t border-slate-700 pt-2 mt-2">
                            <p className="text-yellow-400 font-medium mb-1">
                              Learning stored ({concludeResult.learning.learning_type})
                            </p>
                            <p className="text-slate-300">{concludeResult.learning.insight}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* ── Learnings Tab ── */}
        {activeTab === 'learnings' && (
          <div>
            {!learningsData?.learnings.length ? (
              <EmptyState
                icon={<BookOpen className="w-12 h-12" />}
                title="No learnings yet"
                description="Conclude experiments to automatically extract and store learnings. Each learning records what content patterns (hooks, CTAs, formats, timing) outperform the baseline."
              />
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-3">
                  {(['hook', 'cta', 'format', 'timing'] as const).map(type => {
                    const count = learningsData.learnings.filter((l: Learning) => l.learning_type === type).length
                    return (
                      <MetricCard key={type} label={type.charAt(0).toUpperCase() + type.slice(1)} value={String(count)} change={0} />
                    )
                  })}
                </div>
                <Card title={`${learningsData.total} Stored Learnings`}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-500 border-b border-surface-border">
                        <th className="text-left pb-2">Type</th>
                        <th className="text-left pb-2">Insight</th>
                        <th className="text-right pb-2">Confidence</th>
                        <th className="text-right pb-2">Applied</th>
                      </tr>
                    </thead>
                    <tbody>
                      {learningsData.learnings.map((l: Learning) => (
                        <tr key={l.id} className="border-b border-surface-border/50">
                          <td className="py-2 pr-3">
                            <Badge color={LEARNING_TYPE_COLOR[l.learning_type] ?? 'slate'}>{l.learning_type}</Badge>
                          </td>
                          <td className="py-2 text-slate-300 text-xs max-w-lg">{l.insight}</td>
                          <td className="py-2 text-right text-xs">
                            <span className={l.confidence >= 0.95 ? 'text-green-400' : l.confidence >= 0.80 ? 'text-yellow-400' : 'text-slate-400'}>
                              {(l.confidence * 100).toFixed(1)}%
                            </span>
                          </td>
                          <td className="py-2 text-right text-slate-400 text-xs">{l.applied_count}×</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* ── Genome Tab ── */}
        {activeTab === 'genome' && (
          <div className="space-y-4">
            {genomeData ? (
              <>
                <div className="grid grid-cols-3 gap-3">
                  {genomeData.summary.best_hook_type && (
                    <MetricCard label="Best Hook Type" value={genomeData.summary.best_hook_type.value}
                      change={genomeData.summary.best_hook_type.lift} unit="% lift" />
                  )}
                  {genomeData.summary.best_format && (
                    <MetricCard label="Best Format" value={genomeData.summary.best_format.value}
                      change={genomeData.summary.best_format.lift} unit="% lift" />
                  )}
                  {genomeData.summary.best_posting_hour && (
                    <MetricCard label="Best Posting Hour" value={genomeData.summary.best_posting_hour.value}
                      change={genomeData.summary.best_posting_hour.lift} unit="% lift" />
                  )}
                </div>
                <Card title={`Content Genome — ${genomeData.total_posts_analysed} posts analysed`}>
                  {genomeData.insights.length === 0 ? (
                    <EmptyState icon={<Dna className="w-12 h-12" />} title="No genome data yet" description={genomeData.note} />
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-slate-500 border-b border-surface-border">
                          <th className="text-left pb-2">Attribute</th>
                          <th className="text-left pb-2">Value</th>
                          <th className="text-right pb-2">Avg Score</th>
                          <th className="text-right pb-2">Samples</th>
                          <th className="text-right pb-2">Lift vs Avg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {genomeData.insights.map((ins: GenomeInsight, i: number) => (
                          <tr key={i} className="border-b border-surface-border/50">
                            <td className="py-2 text-slate-400 text-xs">{ins.attribute}</td>
                            <td className="py-2 font-medium">{ins.value}</td>
                            <td className="py-2 text-right text-xs text-slate-300">{ins.avg_performance.toFixed(3)}</td>
                            <td className="py-2 text-right text-xs text-slate-400">{ins.sample_count}</td>
                            <td className="py-2 text-right">
                              <span className={ins.lift_vs_average >= 0 ? 'text-green-400 text-xs' : 'text-red-400 text-xs'}>
                                {ins.lift_vs_average >= 0 ? '+' : ''}{ins.lift_vs_average.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card>
              </>
            ) : (
              <EmptyState icon={<Dna className="w-12 h-12" />} title="Loading genome data…" description="Analysing your content performance patterns." />
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-surface-card border border-surface-border rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">New Experiment</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Experiment Name</label>
                <input type="text" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Q1 Hook Test"
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Hypothesis (optional)</label>
                <input type="text" value={form.hypothesis}
                  onChange={e => setForm(f => ({ ...f, hypothesis: e.target.value }))}
                  placeholder="Question hooks will outperform statement hooks"
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Variant Draft IDs (comma-separated, first = control)</label>
                <input type="text" value={form.variant_draft_ids}
                  onChange={e => setForm(f => ({ ...f, variant_draft_ids: e.target.value }))}
                  placeholder="draft-id-1, draft-id-2"
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Success Metric</label>
                  <select value={form.metric} onChange={e => setForm(f => ({ ...f, metric: e.target.value }))}
                    className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm">
                    {METRICS.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Min samples/variant</label>
                  <input type="number" value={form.min_sample_size}
                    onChange={e => setForm(f => ({ ...f, min_sample_size: parseInt(e.target.value) || 100 }))}
                    className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowCreate(false)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-sm py-2 rounded-lg">Cancel</button>
                <button onClick={() => createMut.mutate()}
                  disabled={createMut.isPending || !form.name.trim() || !form.variant_draft_ids.trim()}
                  className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm py-2 rounded-lg">
                  {createMut.isPending ? 'Creating…' : 'Create Experiment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
